import { type ReactNode, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import {
  DEFAULT_EFFORT,
  DEFAULT_MODEL,
  EFFORT_OPTS,
  MODEL_OPTS,
} from "@tinsmith/model-options";

import styles from "./NewAgent.module.css";
import { useDocumentTitle } from "../../chrome/useDocumentTitle";
import { Button } from "../../components/Button";
import {
  AttachmentChips,
  useComposerAttachments,
} from "../../components/composerAttachments";
import {
  Menu,
  MenuCheck,
  MenuContent,
  MenuHint,
  MenuItem,
  MenuTrigger,
} from "../../components/Menu";
import { RepoAvatar } from "../../components/RepoAvatar";
import { Select } from "../../components/Select";
import { ApiError } from "../../data/api/client";
import { useCurrentWorkspace } from "../../data/api/currentWorkspace";
import {
  repoLabel,
  useRepoSettings,
  useRepositories,
} from "../../data/api/repositories";
import { createTask } from "../../data/api/tasks";
import { useWorkspaceSettings } from "../../data/api/workspace";
import { Icon } from "../../icons";
import { cx } from "../../lib/cx";
import { useNav } from "../../routing/useNav";

const messageFor = (err: unknown): string => {
  if (err instanceof ApiError) {
    return err.code === "network" ? "Can't reach the server." : err.message;
  }
  return "Couldn't launch the agent. Please try again.";
};

type OptDropdownProps = {
  readonly icon: ReactNode;
  readonly label: string;
  readonly onPick: (value: string) => void;
  readonly options: ReadonlyArray<{
    readonly hint?: string;
    readonly value: string;
  }>;
  readonly value: string;
};

const OptDropdown = ({
  icon,
  label,
  onPick,
  options,
  value,
}: OptDropdownProps) => (
  <Menu>
    <MenuTrigger asChild>
      <button className={styles.optBtn} type="button">
        {icon}
        <span className={styles.optKey}>{label}:</span>
        <span className={styles.optVal}>{value}</span>
        <Icon.ChevDown size={11} style={{ opacity: 0.5 }} />
      </button>
    </MenuTrigger>
    <MenuContent side="top">
      {options.map((o) => (
        <MenuItem
          active={o.value === value}
          key={o.value}
          onSelect={() => onPick(o.value)}
        >
          <span className={styles.optValue}>{o.value}</span>
          {o.hint ? <MenuHint>{o.hint}</MenuHint> : null}
          {o.value === value && <MenuCheck />}
        </MenuItem>
      ))}
    </MenuContent>
  </Menu>
);

export const NewAgent = () => {
  const { repoId } = useParams();
  const nav = useNav();
  const { repositories } = useRepositories();
  const { workspaceId } = useCurrentWorkspace();
  const { settings: wsSettings } = useWorkspaceSettings(workspaceId);

  const [text, setText] = useState("");
  const [modelOverride, setModelOverride] = useState<string | null>(null);
  const [effortOverride, setEffortOverride] = useState<string | null>(null);
  const [selectedRepoId, setSelectedRepoId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const paramRepo = repoId
    ? repositories.find((r) => r.id === repoId)
    : undefined;
  const effectiveSelected = selectedRepoId ?? repositories[0]?.id ?? "";
  const targetRepo =
    paramRepo ?? repositories.find((r) => r.id === effectiveSelected);
  // The browser tab names the repo the agent would launch into, so it follows
  // the picker on the global /new page.
  useDocumentTitle("New agent", targetRepo ? repoLabel(targetRepo) : repoId);

  // Repo override > workspace default > hardcoded fallback, matching how the
  // server itself resolves a task's model/effort when the create body omits
  // them (POST /tasks).
  const { settings: repoSettings } = useRepoSettings(targetRepo?.id ?? null);
  const defaultModel =
    repoSettings?.default_model ?? wsSettings?.default_model ?? DEFAULT_MODEL;
  const defaultEffort =
    repoSettings?.default_effort ??
    wsSettings?.default_effort ??
    DEFAULT_EFFORT;
  const model = modelOverride ?? defaultModel;
  const effort = effortOverride ?? defaultEffort;

  const repoOptions = repositories.map((r) => ({
    label: repoLabel(r),
    value: r.id,
  }));
  const selectedRepoObj = targetRepo;

  // Files for the opening prompt (chat.md §Composer UI): same machinery as
  // the task chat's composer, claimed by `POST /tasks` onto the sequence-1
  // message and handed to the agent with its first prompt. Uploads are scoped
  // to the target repo's workspace — which is why they can start before the
  // task exists.
  const {
    addFiles,
    attachments: fileAttachments,
    clear: clearAttachments,
    dragOver,
    readyAttachments,
    removeChip,
    retryChip,
    setDragOver,
    uploadsSettled,
  } = useComposerAttachments(targetRepo?.workspace, !busy);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // A launch needs a prompt — files alone name no goal — and waits for every
  // chip to settle, same as the chat composer's send.
  const canLaunch =
    Boolean(text.trim()) && Boolean(targetRepo) && !busy && uploadsSettled;

  const launch = async (): Promise<void> => {
    if (!canLaunch || !targetRepo) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await createTask({
        attachmentIds: readyAttachments.map((a) => a.uploaded!.public_id),
        effort,
        model,
        prompt: text.trim(),
        repositoryId: targetRepo.id,
        workspaceId: targetRepo.workspace,
      });
      clearAttachments();
      // Launching is the start of a conversation, not a filing action: land on
      // the new task's own page, where the agent's first messages stream in as
      // its sandbox provisions, rather than on the board it is one card of.
      nav.task(targetRepo.id, created.task.display_id);
    } catch (err) {
      setError(messageFor(err));
      setBusy(false);
    }
  };

  const handleLaunch = (): void => {
    void launch();
  };

  return (
    <div className={styles.compose}>
      {!paramRepo && (
        <div className={styles.repoPick}>
          <RepoAvatar org={selectedRepoObj?.org ?? "?"} size={30} />
          <Select
            className={styles.repoSelect}
            onValueChange={setSelectedRepoId}
            options={repoOptions}
            value={effectiveSelected}
          />
        </div>
      )}
      <div
        className={cx(styles.shell, dragOver && styles.shellDrop)}
        onDragLeave={() => setDragOver(false)}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes("Files")) {
            e.preventDefault();
            setDragOver(true);
          }
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          addFiles(e.dataTransfer.files);
        }}
      >
        <textarea
          autoFocus
          className={styles.input}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (
              e.key === "Enter" &&
              !e.shiftKey &&
              !e.nativeEvent.isComposing
            ) {
              e.preventDefault();
              handleLaunch();
            }
          }}
          onPaste={(e) => {
            // The cmd+V-a-screenshot case: clipboard files become chips;
            // pasted text falls through to the textarea as ever.
            const files = Array.from(e.clipboardData.files);
            if (files.length > 0) {
              e.preventDefault();
              addFiles(files);
            }
          }}
          placeholder={
            "e.g. Investigate why /v1/agents returns 502 under load.\nReproduce with vegeta against staging, then propose a fix."
          }
          value={text}
        />
        <AttachmentChips
          attachments={fileAttachments}
          onRemove={removeChip}
          onRetry={retryChip}
        />
        <div className={styles.controls}>
          <input
            hidden
            multiple
            onChange={(e) => {
              addFiles(e.target.files ?? []);
              e.target.value = "";
            }}
            ref={fileInputRef}
            type="file"
          />
          <button
            className={styles.optBtn}
            onClick={() => fileInputRef.current?.click()}
            title="Attach files"
            type="button"
          >
            <Icon.Paperclip size={12} />
            <span className={styles.optKey}>files</span>
          </button>
          <OptDropdown
            icon={<Icon.Spark size={12} />}
            label="model"
            onPick={setModelOverride}
            options={MODEL_OPTS}
            value={model}
          />
          <OptDropdown
            icon={<Icon.Zap size={12} />}
            label="effort"
            onPick={setEffortOverride}
            options={EFFORT_OPTS}
            value={effort}
          />
          <div className={styles.grow} />
          {error ? <span className={styles.launchError}>{error}</span> : null}
          <Button
            disabled={!canLaunch}
            onClick={handleLaunch}
            size="sm"
            style={{ opacity: canLaunch ? 1 : 0.5 }}
            variant="accent"
          >
            <Icon.Zap size={13} /> {busy ? "Launching…" : "Launch agent"}
          </Button>
        </div>
      </div>
      {/* Below the shell rather than beside the button: in the controls row it
          competed for the same line and pushed "Launch agent" onto its own. */}
      <div className={styles.hintRow}>
        <span className={styles.launchHint}>↵ to launch, ⇧↵ for new line</span>
      </div>
    </div>
  );
};
